import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { BarChart3, ExternalLink, FileBarChart } from 'lucide-react';
import { useEffect, useState } from 'react';

import { EmptyState } from '@/presentation/components/index.js';
import type { ProjectController } from '@/presentation/controllers/project-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';

interface Props {
    controller: ProjectController;
}

const IFRAME_HEIGHT = 'calc(100vh - 260px)';

export function ReportsPage({ controller }: Props) {
    const { translate, useProjectStore } = useApp();
    const selectedProject = useProjectStore((s) => s.selectedProject);

    const [reportsUrl, setReportsUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    useEffect(() => {
        if (!selectedProject) {
            setReportsUrl(null);
            setLoadError(null);
            setLoading(false);
            return;
        }
        let active = true;
        setLoading(true);
        setLoadError(null);
        controller
            .loadProjectById(selectedProject.id)
            .then((result) => {
                if (!active) {
                    return;
                }
                result.fold(
                    (err) => {
                        setReportsUrl(null);
                        setLoadError(err.message);
                    },
                    (project) => setReportsUrl(project.reportsUrl)
                );
            })
            .catch((err: unknown) => {
                if (!active) {
                    return;
                }
                setReportsUrl(null);
                setLoadError((err as Error)?.message ?? translate('errors.generic'));
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });
        return () => {
            active = false;
        };
    }, [controller, selectedProject, translate]);

    const renderContent = () => {
        if (!selectedProject) {
            return <EmptyState icon={<BarChart3 size={28} />} title={translate('reports.selectProjectHint')} />;
        }
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={32} />
                </Box>
            );
        }
        if (loadError) {
            return <EmptyState icon={<FileBarChart size={28} />} title={loadError} />;
        }
        if (reportsUrl) {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Stack direction="row" justifyContent="flex-end">
                        <Button
                            startIcon={<ExternalLink size={16} />}
                            href={reportsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            sx={{ textTransform: 'none' }}
                        >
                            {translate('reports.openInNewTab')}
                        </Button>
                    </Stack>
                    <Box
                        component="iframe"
                        src={reportsUrl}
                        sandbox="allow-scripts allow-popups"
                        title={translate('reports.title')}
                        sx={{ width: '100%', height: IFRAME_HEIGHT, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}
                    />
                </Box>
            );
        }
        return <EmptyState icon={<FileBarChart size={28} />} title={translate('reports.noLink')} />;
    };

    return (
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h5" fontWeight={700} color="text.primary">
                {translate('reports.title')}
            </Typography>

            {renderContent()}
        </Box>
    );
}
